import { btnLoading, getElementById } from '@/helpers/DOM'
import { clearHighlightFormError } from './highlight_form_invalid'
import * as Converter from './converter'
import * as Handler from './handle_errors'

/** @type Object<string,string> */
const headers = {
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': 'application/json',
    'Content-Type': 'application/json',
}

const prefix = '/api'

let inputToken = document.querySelector('meta[name="csrf-token"]')

if (inputToken !== null) {
    headers['X-CSRF-TOKEN'] = inputToken.getAttribute('content')
}

function resetForm(form) {
    getElementById(form).reset()
}

function checkStatus(response) {
    if(response.status >= 200 && response.status < 300) {
        return response.json().catch(() => '')
    }

    return response.json().catch(() => Promise.reject({
        status: response.status,
        statusText: response.statusText,
    })).then(data => Promise.reject({
        status: response.status,
        statusText: response.statusText,
        data
    }))
}

function mergePrefix(url)
{
    return prefix + (url.substring(0,1) === '/' ? url : '/' + url)
}

/**
 * @typedef ApiOpts
 *
 * @property {Array|string|int} [ignoreCode] Flag para indicar quais erros não serão tratados automaticamente pela função
 *                                            Podenndo ser '*' para indicar todos, um array com cada codigo http, ou um único código
 * @property {Element} [button]  Um elemento no qual será desabilitado e inserido um loading
 * @property {string|Element} [form]  Formulário no qual contem os dados da requisição. Será usado para capturar
 *                                    os dados dos inputs automaticamente, caso o opts.body e opts.data não tenha sido informado.
 *                                    E será usado para exibir os erros de validações para cada input do formulário.
 * @property {Object|FormData} [data]  Os dados que será enviado para requisição, mas que devem ser tratados antes,
 *                                     diferente do opts.body que é enviado raw
 *                                     Caso seja um objeto, será apenas convertido para JSON, e caso seja FormData, será
 *                                     primeiro convertido para um objeto key-value e depois convertido para JSON
 *                                     Caso o metodo da requisição seja GET, o opts.data será inserido como parametros na URL
 * @property {Object} [dataDefault] Lista de objetos que caso não existam no 'data' vão ser inclusos com seu valor default
 * @property {boolean} [resetForm]
 * @property {boolean} [withPrefix]
 * @property {Object<string,string>} [headers]
 */

/**
 * Função para requisição de API da aplicação, no qual trata os codigos de erro http, validações 422, e conversões
 * de dados da requisição
 *
 * @param  {string} urlPath
 * @param  {RequestInit & ApiOpts} [opts]  Opções default do fetch, mais algumas opções adicionais descritas a baixo
 *
 * @return Promise
 */
export default function (urlPath, opts = {}) {
    if (opts.withPrefix !== false) {
        urlPath = mergePrefix(urlPath)
    }

    opts.headers =  opts.headers ? { ...headers, ...opts.headers } : { ...headers }

    // Aplicar loading ao button caso informado
    if (opts.button) {
        btnLoading.start(opts.button)
    }

    if (opts.form) {
        // Caso tenha passado o ID, substitui o ID pelo Elemento form, para ser mais rapido as demais funções
        opts.form = getElementById(opts.form)

        // Limpa os erros de validação exibidos no formulário, caso o 'form' tenha sido informado
        clearHighlightFormError(opts.form)

        // Caso o 'form' tenha sido informado E nõa tenha sido informado o 'body' E o 'data'
        // será pego automaticamente os dados do formulário através do FormData
        if(!opts.body && !opts.data) {
            opts.data = new FormData(getElementById(opts.form))
        }
    }

    // Caso o 'body' não tenha sido informado, e no 'data' tenha sido inserido um FormData,
    // será convertido ele para Object, pois a API por default usa content-type=Aplication/json
    // no qual não converte automaticamente o FormData
    if (!opts.body && opts.data instanceof FormData) {
        opts.data = Converter.convertFormDataToObject(opts.data)
    }

    if (opts.dataDefault) {
        opts.data = {
            ...opts.dataDefault,
            ...opts.data
        }
    }

    // Verifica se a requisição é do tipo GET, e se for, será inserido nos parametros da urlPath o object informado no 'data'
    if (!opts.hasOwnProperty('method') || opts.method === 'GET') {
        if (typeof opts.data === 'object') {
            let url = new URL(urlPath, window.location.origin)

            Converter.convertFormDataToUrl(url, opts.data)

            urlPath = url.href
        }

    // Caso não seja uma requisição GET, e foi informado o 'data' mas não o 'body',
    // será convertido o objeto do 'data' para json e inserido no campo 'body'
    } else if (!opts.body && opts.data) {
        opts.body = JSON.stringify(opts.data)

    // Caso não seja uma requisição GET, e foi informado o 'body' ou não foi informado o 'data'
    // e o 'body' é do tipo FormData, será removido o header content-type para o browser informar automaticamente
    // por conta do boundary
    } else if(opts.body instanceof FormData) {
        delete opts.headers['Content-Type']
    }

    return fetch(urlPath, opts)
        .catch(Handler.handleNetworkIssues)
        .then(checkStatus)
        .then(response => {
            if(opts.resetForm) {
                resetForm(opts.form)
            }

            return Promise.resolve(response)
        })
        .catch(e => Handler.handleErrors(e, opts.ignoreCode, opts.form))
        .finally(() => {
            if (opts.button) {
                btnLoading.finish(opts.button)
            }
        })
}
